import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, onMounted, ref, watch } from "vue";
import {
  ARTIFACT_GENERATION_ONBOARDING,
  ONBOARDING_GENERATE_DURATION,
  ONBOARDING_GENERATE_ERROR,
  ONBOARDING_GENERATE_LARGE,
  ONBOARDING_GENERATE_MESSAGE,
  ONBOARDING_GENERATE_SUCCESS,
} from "@/util";
import {
  artifactStore,
  onboardingApiStore,
  onboardingStore,
  projectApiStore,
} from "@/hooks";
import {
  FlexBox,
  Separator,
  TextButton,
  Typography,
  List,
  ListItem,
} from "@/components/common";
import CalloutSubStep from "./CalloutSubStep.vue";
import JobLoadingSubStep from "./JobLoadingSubStep.vue";


/**
 * The onboarding step to generate data.
 */
const __default__ = {
  name: "GenerateStep",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const status = ref<"initial" | "loading" | "success" | "error">("initial");

const fileCount = computed(() => artifactStore.allArtifacts.length);
const totalArtifactCount = computed(() => onboardingStore.cost?.credits || 0);
const documentCount = computed(
  () => totalArtifactCount.value - fileCount.value
);

const codeFiles = computed(
  () =>
    `${fileCount.value} Files | ${documentCount.value} Generated Documents (Estimate)`
);

const dollars = computed(() =>
  onboardingStore.cost ? Math.floor(onboardingStore.cost.price / 100) : 0
);
const cents = computed(() =>
  onboardingStore.cost
    ? String(Math.floor(onboardingStore.cost.price % 100)).padStart(2, "0")
    : "00"
);
const generateCost = computed(() =>
  onboardingStore.cost
    ? `$${dollars.value}.${cents.value} (80% Early Access Discount)`
    : ""
);

const generateLabel = computed(() =>
  onboardingStore.displayBilling
    ? "Checkout & Generate"
    : "Generate Documentation"
);

/**
 * Updates the status when the job changes.
 */
function updateStatus() {
  const jobStatus = onboardingStore.uploadedJob?.status;
  const correctJobType = onboardingStore.isGenerationJob;

  if ((correctJobType && jobStatus === "FAILED") || onboardingStore.error) {
    status.value = "error";
  } else if (correctJobType && jobStatus === "IN_PROGRESS") {
    status.value = "loading";
  } else if (
    (correctJobType && jobStatus === "COMPLETED") ||
    onboardingStore.generationCompleted
  ) {
    status.value = "success";
    onboardingStore.generationCompleted = true;
  }
}

onMounted(updateStatus);

watch(
  () => onboardingStore.error,
  (error) => {
    if (!error) return;
    updateStatus();
  }
);

watch(() => onboardingStore.uploadedJob, updateStatus);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (status.value !== 'success')
      ? (_openBlock(), _createBlock(_unref(Typography), {
          key: 0,
          el: "div",
          value: _unref(ONBOARDING_GENERATE_MESSAGE)
        }, null, 8, ["value"]))
      : _createCommentVNode("", true),
    (status.value !== 'success')
      ? (_openBlock(), _createBlock(_unref(Typography), {
          key: 1,
          el: "div",
          secondary: "",
          value: _unref(ONBOARDING_GENERATE_DURATION)
        }, null, 8, ["value"]))
      : _createCommentVNode("", true),
    (status.value === 'initial')
      ? (_openBlock(), _createBlock(_unref(FlexBox), {
          key: 2,
          column: "",
          align: "center",
          t: "4"
        }, {
          default: _withCtx(() => [
            (_unref(onboardingStore).blockGeneration)
              ? (_openBlock(), _createBlock(CalloutSubStep, {
                  key: 0,
                  icon: "payment",
                  message: _unref(ONBOARDING_GENERATE_LARGE)
                }, null, 8, ["message"]))
              : (_openBlock(), _createBlock(_unref(List), {
                  key: 1,
                  class: "full-width"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(ListItem), {
                      color: "primary",
                      title: "Project Size",
                      subtitle: codeFiles.value,
                      icon: "code"
                    }, null, 8, ["subtitle"]),
                    _createVNode(_unref(Separator), { inset: "" }),
                    _createVNode(_unref(ListItem), {
                      color: "primary",
                      title: "Generating Documents",
                      subtitle: _unref(ARTIFACT_GENERATION_ONBOARDING).join(', '),
                      icon: "create-artifact"
                    }, null, 8, ["subtitle"]),
                    (_unref(onboardingStore).displayBilling)
                      ? (_openBlock(), _createBlock(_unref(Separator), {
                          key: 0,
                          inset: ""
                        }))
                      : _createCommentVNode("", true),
                    (_unref(onboardingStore).displayBilling)
                      ? (_openBlock(), _createBlock(_unref(ListItem), {
                          key: 1,
                          color: "primary",
                          title: "Generation Cost",
                          subtitle: generateCost.value,
                          icon: "payment"
                        }, null, 8, ["subtitle"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })),
            _createVNode(_unref(FlexBox), { t: "4" }, {
              default: _withCtx(() => [
                _createVNode(_unref(TextButton), {
                  text: "",
                  color: "gradient",
                  class: "bd-gradient",
                  icon: "generate-artifacts",
                  disabled: _unref(onboardingStore).blockGeneration || _unref(onboardingStore).error,
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(onboardingApiStore).handleGenerateDocumentation()))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(generateLabel.value), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (status.value === 'loading')
      ? (_openBlock(), _createBlock(JobLoadingSubStep, { key: 3 }))
      : _createCommentVNode("", true),
    (status.value === 'success')
      ? (_openBlock(), _createBlock(_unref(FlexBox), {
          key: 4,
          column: "",
          align: "center",
          y: "4"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(TextButton), {
              text: "",
              color: "gradient",
              class: "bd-gradient",
              icon: "download",
              loading: _unref(projectApiStore).saveProjectLoading,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(onboardingApiStore).handleCloseOnboarding('export')))
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Export as CSV ")
              ]),
              _: 1
            }, 8, ["loading"]),
            _createVNode(_unref(FlexBox), {
              align: "center",
              justify: "center",
              "full-width": ""
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Separator), { style: {"width":"40px"} }),
                _createVNode(_unref(Typography), {
                  secondary: "",
                  el: "div",
                  class: "q-ma-sm",
                  value: "OR"
                }),
                _createVNode(_unref(Separator), { style: {"width":"40px"} })
              ]),
              _: 1
            }),
            _createVNode(_unref(TextButton), {
              text: "",
              color: "gradient",
              class: "bd-gradient",
              icon: "view-tree",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(onboardingApiStore).handleCloseOnboarding('view')))
            }, {
              default: _withCtx(() => [
                _createTextVNode(" View in SAFA ")
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (status.value === 'success')
      ? (_openBlock(), _createBlock(CalloutSubStep, {
          key: 5,
          status: "success",
          message: _unref(ONBOARDING_GENERATE_SUCCESS)
        }, null, 8, ["message"]))
      : _createCommentVNode("", true),
    (status.value === 'error')
      ? (_openBlock(), _createBlock(CalloutSubStep, {
          key: 6,
          status: "error",
          message: _unref(ONBOARDING_GENERATE_ERROR)
        }, null, 8, ["message"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})